import { useEffect, useState, useCallback } from 'react'
import { lumi } from '@/lib/lumi'
import { seoPages, SEOPage } from '@/data/seoPages'

// ─── Type du document Lumi ──────────────────────────────────────────────────
export interface SeoPageDoc {
  _id: string
  kind: 'override' | 'custom'
  pageId: string
  path?: string
  slug?: string
  parent?: string
  isPillar?: boolean
  silo?: 'BNSSA' | 'MNS'
  keyword?: string
  title?: string
  metaDescription?: string
  h1?: string
  intro?: string
  heroImage?: string
  sections?: { title: string; content: string }[]
  color?: 'red' | 'blue'
  isActive?: boolean
  creator: string
  createdAt: string
  updatedAt: string
}

// ─── Construit un SEOPage complet depuis un doc 'custom' ────────────────────
function customDocToPage(doc: SeoPageDoc): SEOPage | null {
  if (
    doc.kind !== 'custom' ||
    !doc.path ||
    !doc.slug ||
    !doc.silo ||
    !doc.keyword ||
    !doc.title ||
    !doc.metaDescription ||
    !doc.h1
  )
    return null
  return {
    id: doc.pageId,
    path: doc.path,
    slug: doc.slug,
    parent: doc.parent,
    isPillar: doc.isPillar ?? false,
    silo: doc.silo,
    keyword: doc.keyword,
    title: doc.title,
    metaDescription: doc.metaDescription,
    h1: doc.h1,
    intro: doc.intro ?? '',
    sections: doc.sections ?? [],
    color: doc.color ?? (doc.silo === 'BNSSA' ? 'red' : 'blue'),
    heroImage: doc.heroImage,
    isActive: doc.isActive !== false,
  }
}

// ─── Applique les overrides aux pages de base ───────────────────────────────
function applyOverrides(basePages: SEOPage[], overrideDocs: SeoPageDoc[]): SEOPage[] {
  const overridesByPageId = new Map<string, SeoPageDoc>()
  overrideDocs.forEach((d) => {
    if (d.kind === 'override' && d.pageId) overridesByPageId.set(d.pageId, d)
  })
  return basePages.map((p) => {
    const o = overridesByPageId.get(p.id)
    if (!o) return p
    const next: SEOPage = { ...p }
    if (typeof o.title === 'string') next.title = o.title
    if (typeof o.metaDescription === 'string') next.metaDescription = o.metaDescription
    if (typeof o.h1 === 'string') next.h1 = o.h1
    if (typeof o.intro === 'string') next.intro = o.intro
    if (typeof o.keyword === 'string') next.keyword = o.keyword
    if (Array.isArray(o.sections)) next.sections = o.sections
    if (typeof o.heroImage === 'string') next.heroImage = o.heroImage || undefined
    if (typeof o.isActive === 'boolean') next.isActive = o.isActive
    return next
  })
}

// ─── Cache global pour permettre une lecture synchrone ──────────────────────
let GLOBAL_DOCS: SeoPageDoc[] = []
let GLOBAL_FETCH_SUCCEEDED = false // ✅ Vrai succès depuis Lumi (pas juste "tenté")
let GLOBAL_LOAD_PROMISE: Promise<SeoPageDoc[]> | null = null
let GLOBAL_LAST_FETCH_AT = 0

// Liste des listeners pour notifier tous les hooks instanciés quand les docs
// arrivent depuis le serveur.
const GLOBAL_LISTENERS = new Set<(state: { docs: SeoPageDoc[]; success: boolean; error: string | null }) => void>()

function notifyAllListeners(state: { docs: SeoPageDoc[]; success: boolean; error: string | null }) {
  GLOBAL_LISTENERS.forEach((fn) => {
    try {
      fn(state)
    } catch (e) {
      console.warn('[useSeoPages] listener failed', e)
    }
  })
}

/**
 * Charge tous les docs SEO depuis Lumi.
 * CRITICAL: Sur mobile, la connexion peut être lente/instable. On implémente:
 * - Un timeout explicite (15s) pour ne pas bloquer indéfiniment.
 * - Un retry automatique avec backoff (3 tentatives).
 * - Une distinction claire entre "tentative faite" et "succès réel".
 */
async function loadAllDocs(forceRefresh = false): Promise<SeoPageDoc[]> {
  if (!forceRefresh && GLOBAL_LOAD_PROMISE) return GLOBAL_LOAD_PROMISE

  GLOBAL_LOAD_PROMISE = (async () => {
    const MAX_ATTEMPTS = 3
    const TIMEOUT_MS = 15000

    let lastError: unknown = null

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      try {
        console.log(`[useSeoPages] loadAllDocs attempt ${attempt}/${MAX_ATTEMPTS}`)

        const fetchPromise = lumi.entities.seo_pages.list({ sort: { updatedAt: -1 } })
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error(`Timeout après ${TIMEOUT_MS}ms`)), TIMEOUT_MS),
        )

        const res = (await Promise.race([fetchPromise, timeoutPromise])) as { list?: SeoPageDoc[] }
        const docs = (res?.list ?? []) as SeoPageDoc[]

        console.log('[useSeoPages] loadAllDocs OK', {
          count: docs.length,
          attempt,
          kinds: docs.reduce<Record<string, number>>((acc, d) => {
            acc[d.kind] = (acc[d.kind] || 0) + 1
            return acc
          }, {}),
        })

        GLOBAL_DOCS = docs
        GLOBAL_FETCH_SUCCEEDED = true
        GLOBAL_LAST_FETCH_AT = Date.now()
        notifyAllListeners({ docs, success: true, error: null })
        return docs
      } catch (err) {
        lastError = err
        console.warn(`[useSeoPages] loadAllDocs attempt ${attempt} failed`, err)
        if (attempt < MAX_ATTEMPTS) {
          // Backoff exponentiel: 500ms, 1500ms
          await new Promise((r) => setTimeout(r, 500 * attempt * attempt))
        }
      }
    }

    // Tous les essais ont échoué
    console.error('[useSeoPages] loadAllDocs failed after all retries', lastError)
    const errorMsg = lastError instanceof Error ? lastError.message : 'Erreur de chargement'
    notifyAllListeners({ docs: GLOBAL_DOCS, success: false, error: errorMsg })
    return GLOBAL_DOCS
  })().finally(() => {
    GLOBAL_LOAD_PROMISE = null
  })

  return GLOBAL_LOAD_PROMISE
}

// Pré-chargement des docs Lumi dès l'import du module côté client.
if (typeof window !== 'undefined') {
  void loadAllDocs()

  // CRITICAL pour mobile: au retour de focus sur l'onglet (cas iOS Safari
  // restauré depuis bfcache, ou retour d'arrière-plan), on force un refresh
  // si le dernier fetch est ancien (>30s) ou n'a jamais réussi.
  // Cela résout le bug "page blanche au refresh sur mobile" car même si le
  // bfcache restaure l'état, on garantit qu'on tente à nouveau de charger.
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      // Mobile (Chrome Android notamment): seuil court (10s) car le navigateur
      // peut restaurer la page depuis bfcache avec des données obsolètes après
      // un simple changement d'onglet/notification.
      const stale = !GLOBAL_FETCH_SUCCEEDED || Date.now() - GLOBAL_LAST_FETCH_AT > 10000
      if (stale && !GLOBAL_LOAD_PROMISE) {
        console.log('[useSeoPages] visibility change → refresh stale data')
        void loadAllDocs(true)
      }
    }
  }
  document.addEventListener('visibilitychange', handleVisibilityChange)

  // CRITICAL pour iOS Safari: l'événement `pageshow` avec `persisted=true`
  // signifie que la page a été restaurée depuis le bfcache (cache page).
  // Dans ce cas, le JS ne re-execute PAS, mais on doit quand même vérifier
  // si nos données sont à jour. Ce listener garantit qu'au refresh F5 mobile
  // après un bfcache, on relance un fetch.
  window.addEventListener('pageshow', (e) => {
    if ((e as PageTransitionEvent).persisted) {
      console.log('[useSeoPages] pageshow persisted (bfcache) → force refresh')
      void loadAllDocs(true)
    }
  })
}

/**
 * Force un refresh si les données sont obsolètes (>10s) ou jamais chargées.
 * Utile sur changement de route côté client (notamment Chrome Android après
 * bfcache) pour garantir que l'affichage reflet étoujours l'état serveur.
 */
export function refreshSeoPagesIfStale(maxAgeMs = 10000) {
  if (typeof window === 'undefined') return
  const stale = !GLOBAL_FETCH_SUCCEEDED || Date.now() - GLOBAL_LAST_FETCH_AT > maxAgeMs
  if (stale && !GLOBAL_LOAD_PROMISE) {
    void loadAllDocs(true)
  }
}

/**
 * Renvoie immédiatement la liste fusionnée à partir du cache.
 * Si rien n'est en cache, déclenche un chargement et renvoie les pages statiques en attendant.
 */
export function getMergedSeoPagesSync(): SEOPage[] {
  if (!GLOBAL_FETCH_SUCCEEDED) {
    void loadAllDocs()
    return seoPages
  }
  const customs: SEOPage[] = GLOBAL_DOCS.filter((d) => d.kind === 'custom')
    .map(customDocToPage)
    .filter((p): p is SEOPage => p !== null)
  return applyOverrides([...seoPages, ...customs], GLOBAL_DOCS)
}

// ─── Hook pour les composants qui ont besoin du cycle complet ───────────────
export function useSeoPages() {
  const [docs, setDocs] = useState<SeoPageDoc[]>(GLOBAL_DOCS)
  // CRITICAL: `loading` est vrai TANT QU'AUCUN FETCH N'A RÉUSSI.
  // On ne se base plus sur GLOBAL_LOADED (= "tentative faite") mais sur
  // GLOBAL_FETCH_SUCCEEDED (= "réponse Lumi reçue avec succès"). Cela
  // garantit que tant qu'on n'a pas reçu les vraies données depuis Lumi,
  // on continue d'afficher le loader au lieu de tomber en 404.
  const [loading, setLoading] = useState<boolean>(!GLOBAL_FETCH_SUCCEEDED)
  const [error, setError] = useState<string | null>(null)

  // S'abonne au cache global pour recevoir les mises à jour de docs.
  useEffect(() => {
    const listener = (state: { docs: SeoPageDoc[]; success: boolean; error: string | null }) => {
      setDocs(state.docs)
      if (state.success) {
        setLoading(false)
        setError(null)
      } else {
        // Échec: on garde le loading false (sinon spinner infini) mais on
        // expose l'erreur pour que SeoPage puisse l'afficher.
        setLoading(false)
        setError(state.error)
      }
    }
    GLOBAL_LISTENERS.add(listener)

    // Si le cache a déjà été rempli AVANT que ce composant ne s'abonne,
    // on synchronise immédiatement.
    if (GLOBAL_FETCH_SUCCEEDED) {
      setDocs(GLOBAL_DOCS)
      setLoading(false)
      setError(null)
    }

    return () => {
      GLOBAL_LISTENERS.delete(listener)
    }
  }, [])

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const list = await loadAllDocs(true)
      setDocs(list)
      setLoading(false)
      console.log('[useSeoPages] refresh OK', { count: list.length })
    } catch (err) {
      console.error('[useSeoPages] refresh failed', err)
      setError(err instanceof Error ? err.message : 'Erreur de chargement')
      setLoading(false)
    }
  }, [])

  // CRITICAL: au mount, on s'assure qu'un chargement est en cours OU a réussi.
  // Si GLOBAL_FETCH_SUCCEEDED est false ET aucune promise en cours, on lance.
  useEffect(() => {
    if (GLOBAL_FETCH_SUCCEEDED) {
      // Cache déjà rempli avec succès → synchronisation immédiate.
      setDocs(GLOBAL_DOCS)
      setLoading(false)
      return
    }
    if (GLOBAL_LOAD_PROMISE) {
      // Pré-chargement déjà en cours → on attend simplement.
      // Le listener ajouté plus haut sera notifié à la résolution.
      return
    }
    // Aucun chargement n'a démarré (ou tous ont échoué) → on relance.
    void loadAllDocs(true)
  }, [])

  // Pages mergées (statiques + custom + overrides)
  const customs: SEOPage[] = docs
    .filter((d) => d.kind === 'custom')
    .map(customDocToPage)
    .filter((p): p is SEOPage => p !== null)
  const mergedPages = applyOverrides([...seoPages, ...customs], docs)

  // ─── Sauvegarde un override ───────────────────────────────────────────────
  const saveOverride = useCallback(
    async (
      pageId: string,
      patch: {
        title?: string
        metaDescription?: string
        h1?: string
        intro?: string
        keyword?: string
        heroImage?: string
        sections?: { title: string; content: string }[]
        isActive?: boolean
      },
      creator: string,
    ) => {
      const now = new Date().toISOString()
      console.log('[useSeoPages] saveOverride START', { pageId, fields: Object.keys(patch), creator })

      let existing: SeoPageDoc | undefined =
        GLOBAL_DOCS.find((d) => d.kind === 'override' && d.pageId === pageId) ||
        docs.find((d) => d.kind === 'override' && d.pageId === pageId)

      if (!existing) {
        try {
          console.log('[useSeoPages] saveOverride: querying server for existing override', { pageId })
          const res = await lumi.entities.seo_pages.list({
            filter: { kind: 'override', pageId },
            limit: 1,
          })
          const found = (res?.list ?? [])[0] as SeoPageDoc | undefined
          if (found) {
            console.log('[useSeoPages] saveOverride: found on server', { _id: found._id })
            existing = found
          }
        } catch (err) {
          console.warn('[useSeoPages] saveOverride: server lookup failed (will fallback to create)', err)
        }
      }

      try {
        if (existing) {
          console.log('[useSeoPages] saveOverride: updating existing doc', { _id: existing._id })
          const updated = await lumi.entities.seo_pages.update(existing._id, {
            ...patch,
            updatedAt: now,
          })
          console.log('[useSeoPages] saveOverride: update OK', { updated })
          const updatedDoc = { ...existing, ...patch, updatedAt: now } as SeoPageDoc
          const nextGlobal = GLOBAL_DOCS.some((d) => d._id === existing!._id)
            ? GLOBAL_DOCS.map((d) => (d._id === existing!._id ? updatedDoc : d))
            : [updatedDoc, ...GLOBAL_DOCS]
          GLOBAL_DOCS = nextGlobal
          setDocs(nextGlobal)
          return updated ?? updatedDoc
        } else {
          console.log('[useSeoPages] saveOverride: creating new override doc')
          const created = await lumi.entities.seo_pages.create({
            kind: 'override',
            pageId,
            ...patch,
            creator,
            createdAt: now,
            updatedAt: now,
          })
          console.log('[useSeoPages] saveOverride: create OK', { created })
          const newDoc = (created ?? {
            _id: `tmp-${Date.now()}`,
            kind: 'override',
            pageId,
            ...patch,
            creator,
            createdAt: now,
            updatedAt: now,
          }) as SeoPageDoc
          const nextGlobal = [newDoc, ...GLOBAL_DOCS]
          GLOBAL_DOCS = nextGlobal
          setDocs(nextGlobal)
          return newDoc
        }
      } catch (err) {
        console.error('[useSeoPages] saveOverride failed', err)
        throw err
      }
    },
    [docs],
  )

  // ─── Réinitialise une page statique (supprime son override) ──────────────
  const resetOverride = useCallback(
    async (pageId: string) => {
      const existing = docs.find((d) => d.kind === 'override' && d.pageId === pageId)
      if (!existing) return
      try {
        await lumi.entities.seo_pages.delete(existing._id)
        const next = docs.filter((d) => d._id !== existing._id)
        GLOBAL_DOCS = next
        setDocs(next)
      } catch (err) {
        console.error('[useSeoPages] resetOverride failed', err)
        throw err
      }
    },
    [docs],
  )

  // ─── Crée une nouvelle page custom ───────────────────────────────────────
  const createCustomPage = useCallback(
    async (page: SEOPage, creator: string) => {
      const now = new Date().toISOString()
      try {
        const created = await lumi.entities.seo_pages.create({
          kind: 'custom',
          pageId: page.id,
          path: page.path,
          slug: page.slug,
          parent: page.parent,
          isPillar: page.isPillar,
          silo: page.silo,
          keyword: page.keyword,
          title: page.title,
          metaDescription: page.metaDescription,
          h1: page.h1,
          intro: page.intro,
          sections: page.sections,
          color: page.color,
          heroImage: page.heroImage,
          isActive: page.isActive !== false,
          creator,
          createdAt: now,
          updatedAt: now,
        })
        const newDoc = (created ?? {
          _id: `tmp-${Date.now()}`,
          kind: 'custom',
          pageId: page.id,
          path: page.path,
          slug: page.slug,
          parent: page.parent,
          isPillar: page.isPillar,
          silo: page.silo,
          keyword: page.keyword,
          title: page.title,
          metaDescription: page.metaDescription,
          h1: page.h1,
          intro: page.intro,
          sections: page.sections,
          color: page.color,
          heroImage: page.heroImage,
          isActive: page.isActive !== false,
          creator,
          createdAt: now,
          updatedAt: now,
        }) as SeoPageDoc
        const next = [newDoc, ...docs]
        GLOBAL_DOCS = next
        setDocs(next)
        return newDoc
      } catch (err) {
        console.error('[useSeoPages] createCustomPage failed', err)
        throw err
      }
    },
    [docs],
  )

  // ─── Supprime une page custom ─────────────────────────────────────────────
  const deleteCustomPage = useCallback(
    async (pageId: string) => {
      const customDoc = docs.find((d) => d.kind === 'custom' && d.pageId === pageId)
      const overrideDoc = docs.find((d) => d.kind === 'override' && d.pageId === pageId)
      try {
        if (customDoc) await lumi.entities.seo_pages.delete(customDoc._id)
        if (overrideDoc) await lumi.entities.seo_pages.delete(overrideDoc._id)
        const next = docs.filter((d) => d.pageId !== pageId)
        GLOBAL_DOCS = next
        setDocs(next)
      } catch (err) {
        console.error('[useSeoPages] deleteCustomPage failed', err)
        throw err
      }
    },
    [docs],
  )

  return {
    docs,
    loading,
    error,
    mergedPages,
    refresh,
    saveOverride,
    resetOverride,
    createCustomPage,
    deleteCustomPage,
    isOverridden: (pageId: string) => docs.some((d) => d.kind === 'override' && d.pageId === pageId),
    isCustomPage: (pageId: string) => docs.some((d) => d.kind === 'custom' && d.pageId === pageId),
    /** True si on a déjà reçu une réponse réussie de Lumi (pas juste tenté). */
    hasFetchedSuccessfully: GLOBAL_FETCH_SUCCEEDED,
  }
}
